import delay from '@redux-saga/delay-p';
import { call, put, takeEvery, all, CallEffect, PutEffect } from 'redux-saga/effects';
import { pollingDelayMs } from 'commons/src/constants';
import { SensorTypes } from 'commons/src/models/commonEnums';
import { toErrorType } from 'commons/src/sagas/isErrorType';
import {
    DeviceActionType,
    FetchPublicDeviceData,
    FetchPublicDeviceResponse,
    fetchPublicDeviceDataSuccess,
    fetchPublicDeviceDataError,
    FetchPublicDeviceSensorData,
    FetchPublicDeviceSensorResponse,
    fetchPublicDeviceSensorDataSuccess,
    fetchPublicDeviceSensorDataError,
    FetchPublicDeviceDataSuccess,
    FetchPublicDeviceDataError,
    FetchPublicDeviceSensorDataSuccess,
    FetchPublicDeviceSensorDataError,
} from '../actions/actions';
import { getPublicDeviceData, getPublicDeviceSensorData } from '../api/publicDevice';
import { visibleSensors } from '../constants';
import { PublicDeviceResponse } from '../models/commons';

type FetchPublicDeviceDataSagaActionType = Generator<
    | CallEffect<FetchPublicDeviceResponse>
    | PutEffect<FetchPublicDeviceDataSuccess>
    | PutEffect<FetchPublicDeviceDataError>,
    void,
    FetchPublicDeviceResponse
>;
export function* fetchPublicDeviceDataSaga({ payload }: FetchPublicDeviceData): FetchPublicDeviceDataSagaActionType {
    try {
        const response: FetchPublicDeviceResponse = yield call(getPublicDeviceData, payload);
        yield put(fetchPublicDeviceDataSuccess(response));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(fetchPublicDeviceDataError(errorAsErrorType.error));
    }
}

type FetchPublicDeviceSensorDataSagaActionType = Generator<
    | CallEffect<{ data: PublicDeviceResponse }>
    | CallEffect
    | PutEffect<FetchPublicDeviceSensorDataSuccess>
    | PutEffect<FetchPublicDeviceSensorDataError>,
    void,
    { data: PublicDeviceResponse }
>;
export function* fetchPublicDeviceSensorDataSaga({
    payload,
}: FetchPublicDeviceSensorData): FetchPublicDeviceSensorDataSagaActionType {
    const { isTest } = payload;
    let returnsError = false;
    while (!returnsError) {
        try {
            const apiResponse = yield call(getPublicDeviceSensorData, payload);
            const sensorData = Object.entries(apiResponse.data)
                .filter(sensor => payload.showAllSensors || visibleSensors.includes(sensor[0] as SensorTypes))
                .map(sensor => ({
                    type: sensor[0] as SensorTypes,
                    value: Math.round(sensor[1] as number),
                }));
            const timeStamp = apiResponse.data.time;
            const response: FetchPublicDeviceSensorResponse = { sensorData, timeStamp };
            yield put(fetchPublicDeviceSensorDataSuccess(response));
            if (isTest === undefined) {
                yield call(delay, pollingDelayMs);
            } else {
                // this is here to prevent the test from running in a loop.
                returnsError = true;
            }
        } catch (error) {
            returnsError = true;
            const errorAsErrorType = toErrorType(error);
            yield put(fetchPublicDeviceSensorDataError(errorAsErrorType.error));
        }
    }
}

export default function* () {
    yield all([
        takeEvery(DeviceActionType.FetchDeviceDataInit, fetchPublicDeviceDataSaga),
        takeEvery(DeviceActionType.FetchDeviceSensorDataInit, fetchPublicDeviceSensorDataSaga),
    ]);
}
